import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Mic, Loader2, Sparkles, RefreshCw, MessageSquare } from "lucide-react";
import PageHead from "@/components/PageHead";
import { MatchRing } from "@/components/ui-bits";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STAR = [
  ["situation", "Situation"],
  ["task", "Task"],
  ["action", "Action"],
  ["result", "Result"],
];

export default function InterviewCopilot() {
  const { user } = useAuth();
  const targetRole = user?.profile?.target_role || user?.profile?.current_role || "Product Manager";

  const [questions, setQuestions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [answer, setAnswer] = useState("");
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const fetchQuestions = async () => {
    setLoadingQuestions(true);
    try {
      const { data } = await api.get("/ai/interview-questions", {
        params: { role: targetRole, count: 5 },
      });
      const qList = data?.questions || [];
      setQuestions(qList);
      if (qList.length > 0) {
        setSelected(qList[0]);
      }
    } catch (err) {
      toast.error("Could not generate questions. Please try again.");
    } finally {
      setLoadingQuestions(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRole]);

  const submit = async () => {
    if (!answer.trim()) {
      toast.error("Please write your answer before requesting feedback.");
      return;
    }
    if (!selected) return;

    setLoading(true);
    try {
      const { data } = await api.post("/ai/interview-feedback", {
        question: selected.question || selected.q,
        answer,
        role: targetRole,
      });
      setFeedback(data);
      toast.success("AI STAR evaluation complete!");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not evaluate answer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHead
        label="Interview Copilot"
        title="Structure your answer with STAR"
        sub="Dynamic interview questions tailored to your target role. Draft your response and receive instant AI STAR scoring."
      >
        <button
          onClick={fetchQuestions}
          disabled={loadingQuestions}
          className="cv-btn-ghost border border-border text-xs flex items-center gap-1.5"
          title="Generate fresh questions"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loadingQuestions ? "animate-spin" : ""}`} />
          New Questions
        </button>
      </PageHead>

      <div className="grid gap-6 lg:grid-cols-3 items-start">
        {/* Left Column: Question Bank */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="label-caps flex items-center gap-1.5">
              <MessageSquare className="h-3.5 w-3.5 text-violet" /> Tailored Questions
            </p>
            <span className="text-xs font-semibold text-muted-foreground">{targetRole}</span>
          </div>

          {loadingQuestions ? (
            <div className="flex min-h-[220px] items-center justify-center rounded-2xl border border-dashed border-border">
              <Loader2 className="h-5 w-5 animate-spin text-violet" />
              <span className="ml-2 text-xs text-muted-foreground">Generating questions…</span>
            </div>
          ) : (
            <div className="space-y-2.5">
              {questions.map((q, idx) => {
                const isCurrent = selected?.id === q.id || (!selected && idx === 0);
                return (
                  <button
                    key={q.id || idx}
                    onClick={() => {
                      setSelected(q);
                      setFeedback(null);
                      setAnswer("");
                    }}
                    data-testid={`iq-${q.id || idx}`}
                    className={`w-full rounded-2xl border p-4 text-left transition-all ${
                      isCurrent
                        ? "border-violet bg-violet/5 shadow-xs"
                        : "border-border hover:bg-secondary"
                    }`}
                  >
                    <span className="rounded-full bg-violet/10 px-2 py-0.5 text-[11px] font-bold text-violet">
                      {q.type || "Behavioral"}
                    </span>
                    <p className="mt-2 text-xs font-semibold leading-relaxed text-foreground">
                      {q.question || q.q}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Column: Active Question + Answer Area + AI Feedback */}
        <div className="lg:col-span-2 space-y-4">
          <div className="card-surface p-5 shadow-sm">
            <p className="font-heading text-sm font-bold text-foreground">
              "{selected?.question || selected?.q || "Select an interview question"}"
            </p>
            {selected?.hint && (
              <p className="mt-1 text-xs text-muted-foreground italic">
                💡 Tip: {selected.hint}
              </p>
            )}

            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              rows={6}
              data-testid="interview-answer"
              placeholder="Structure your answer using STAR (Situation, Task, Action, Result)..."
              className="mt-3 w-full resize-none rounded-xl border border-border bg-background p-3 text-xs leading-relaxed outline-none focus:border-violet"
            />

            <button
              onClick={submit}
              disabled={loading || !selected}
              data-testid="interview-submit"
              className="cv-btn-primary mt-3 py-2.5 px-5 text-xs font-semibold flex items-center gap-1.5"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Evaluating Answer…
                </>
              ) : (
                <>
                  <Mic className="h-4 w-4" /> Get STAR Feedback
                </>
              )}
            </button>
          </div>

          {!feedback && !loading && (
            <div className="flex min-h-[160px] flex-col items-center justify-center rounded-3xl border border-dashed border-border p-6 text-center">
              <Sparkles className="h-7 w-7 text-violet mb-2" />
              <p className="text-xs text-muted-foreground">
                Draft and submit your response to receive structured STAR scoring and AI coaching recommendations.
              </p>
            </div>
          )}

          {feedback && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
              {/* STAR Dimension Scores */}
              <div className="card-surface grid grid-cols-2 gap-4 p-5 sm:grid-cols-4 shadow-sm">
                {STAR.map(([k, label]) => (
                  <div key={k} className="text-center">
                    <MatchRing
                      value={feedback.star_scores?.[k] ?? 75}
                      size={60}
                      stroke={6}
                      color="hsl(var(--chart-3))"
                    />
                    <p className="mt-2 text-xs font-bold">{label}</p>
                    <span className="text-[11px] text-muted-foreground">
                      {feedback.star_scores?.[k] ?? 75}%
                    </span>
                  </div>
                ))}
              </div>

              {/* Strengths & Improvements */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="card-surface p-4 shadow-sm">
                  <p className="label-caps mb-2 text-lime">Strengths Identified</p>
                  <ul className="space-y-1.5 text-xs text-muted-foreground">
                    {(feedback.strengths || []).map((s, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <span className="text-lime font-bold">✓</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="card-surface p-4 shadow-sm">
                  <p className="label-caps mb-2 text-coral">Areas to Sharpen</p>
                  <ul className="space-y-1.5 text-xs text-muted-foreground">
                    {(feedback.improvements || []).map((s, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <span className="text-coral font-bold">→</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* AI Model Answer */}
              {feedback.model_answer && (
                <div className="card-surface p-5 shadow-sm">
                  <p className="label-caps mb-2 text-violet">Exemplary STAR Model Answer</p>
                  <p className="text-xs text-foreground leading-relaxed bg-secondary/50 p-3 rounded-xl border border-border">
                    {feedback.model_answer}
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
